import { useEffect, useRef, useState } from "react";
import { useUser } from "@/redux/user";
import {
  Assignment,
  getProductAssignment,
  ProductFeature,
  productExperimentsEnabled,
  recordProductExposure,
} from "./productExperiments";

// Both visible variants and hidden holdouts use the same opportunity boundary.
export default function useProductExperiment(
  feature: ProductFeature,
  baseline: boolean,
  eligible = true,
) {
  const { id } = useUser();
  const ref = useRef<HTMLDivElement>(null);
  const [reply, setReply] = useState<{
    identity: string;
    assignment: Assignment | null;
  } | null>(null);
  const [inView, setInView] = useState(false);
  const recorded = useRef(new Set<string>());
  const identity = `${feature}:${id}`;
  const enabled = productExperimentsEnabled && eligible && id != null;
  const assignment = reply?.identity === identity ? reply.assignment : null;
  const ready = !enabled || reply?.identity === identity;
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false,
      running = false;
    async function refresh() {
      if (running) return;
      running = true;
      try {
        const selected = await getProductAssignment(feature);
        if (!cancelled) setReply({ identity, assignment: selected });
      } catch {
        if (!cancelled) setReply({ identity, assignment: null });
      } finally {
        running = false;
      }
    }
    void refresh();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, 60000);
    const focus = () => void refresh();
    window.addEventListener("focus", focus);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", focus);
    };
  }, [enabled, feature, identity]);
  useEffect(() => {
    setInView(false);
    if (
      !enabled ||
      !ready ||
      !ref.current ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [enabled, ready, identity]);
  useEffect(() => {
    if (
      !enabled ||
      !inView ||
      !assignment ||
      recorded.current.has(`${identity}:${assignment.key}`)
    )
      return;
    let cancelled = false;
    const key = `${identity}:${assignment.key}`;
    void recordProductExposure(assignment)
      .then(() => {
        if (!cancelled) recorded.current.add(key);
      })
      .catch(() => {
        /* Retry with the next refreshed token; gameplay continues. */
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, inView, assignment, identity]);
  return {
    ref,
    ready,
    value: assignment ? assignment.value : baseline,
    active: !!assignment,
  };
}
